import { c as _c } from "react/compiler-runtime";
import { Spinner } from '@common/components';
import { cn } from '@core/utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { UnauthInfiniteQueryOptions } from '@/api';
import { updateResponsiveDialogConfig } from '@/stores/ResponsiveDialogStore';
import { filledInfiniteQueryOptions } from '@/utils/filledInfiniteQueryOptions';
import { ReviewDetailContent } from '../ReviewDetailContent';
import { PhotoReviewItem } from './PhotoReviewItem';
import { SideNavButtons } from './SideNavButtons';
interface PhotoReviewsModalContentProps {
  photoReviewList: {
    fileUrl: string;
    reviewId: number;
  }[];
  photoReviewCount: number;
  productNumber?: string;
  institutionId?: number;
  initialReviewId?: number;
}
const INITIAL_PAGE = 1;
const INITIAL_SIZE = 10;
export const PhotoReviewsModalContent = t0 => {
  const $ = _c(37);
  const {
    productNumber,
    institutionId,
    initialReviewId
  } = t0;
  const containerRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      left: {
        top: 0,
        left: 0
      },
      right: {
        top: 0,
        right: 0
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [buttonPositions, setButtonPositions] = useState(t1);
  const [selectedReviewId, setSelectedReviewId] = useState(initialReviewId ?? null);
  let t2;
  if ($[1] !== institutionId || $[2] !== productNumber) {
    t2 = filledInfiniteQueryOptions(UnauthInfiniteQueryOptions["get:/wello/v2/allows/home/gift-review/photo"]({
      query: {
        productNumber,
        institutionId,
        page: INITIAL_PAGE,
        size: INITIAL_SIZE
      }
    }));
    $[1] = institutionId;
    $[2] = productNumber;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = !!productNumber || !!institutionId;
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      ...t2,
      enabled: t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching
  } = useInfiniteQuery(t4);
  const {
    ref,
    inView
  } = useInView();
  const infinitePhotoReviewList = data?.pages.flatMap(_temp) ?? [];
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      if (!containerRef.current) {
        return;
      }
      const rect = containerRef.current.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      setButtonPositions({
        left: {
          top: centerY,
          left: rect.left - 120
        },
        right: {
          top: centerY,
          right: window.innerWidth - rect.right - 120
        }
      });
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const updateButtonPositions = t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      updateButtonPositions();
      const resizeObserver = new ResizeObserver(() => {
        updateButtonPositions();
      });
      resizeObserver.observe(container);
      const handleResize = () => updateButtonPositions();
      const handleScroll = () => updateButtonPositions();
      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleScroll);
      return () => {
        resizeObserver.disconnect();
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", handleScroll);
      };
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  useLayoutEffect(t6, [infinitePhotoReviewList.length, selectedReviewId]);
  let t7;
  if ($[9] !== fetchNextPage || $[10] !== inView || $[11] !== isFetching) {
    t7 = () => {
      if (!isFetching && inView) {
        fetchNextPage();
      }
    };
    $[9] = fetchNextPage;
    $[10] = inView;
    $[11] = isFetching;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== fetchNextPage || $[14] !== hasNextPage || $[15] !== inView || $[16] !== isFetching) {
    t8 = [inView, isFetching, fetchNextPage, hasNextPage];
    $[13] = fetchNextPage;
    $[14] = hasNextPage;
    $[15] = inView;
    $[16] = isFetching;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  useEffect(t7, t8);
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = reviewId => {
      setSelectedReviewId(reviewId);
      updateResponsiveDialogConfig({
        modalHeader: "\uD3EC\uD1A0\uD6C4\uAE30"
      });
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleDetailReviewId = t9;
  let t10;
  if ($[19] !== selectedReviewId) {
    t10 = item => item.reviewId === selectedReviewId;
    $[19] = selectedReviewId;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const selectedReviewIndex = infinitePhotoReviewList.findIndex(t10);
  const prevReviewIndex = selectedReviewIndex - 1;
  const nextReviewIndex = selectedReviewIndex + 1;
  const isPrevDisabled = prevReviewIndex < 0;
  const isNextDisabled = nextReviewIndex >= infinitePhotoReviewList.length;
  const isLastReview = selectedReviewIndex === infinitePhotoReviewList.length - 1;
  const handlePrevClick = () => {
    if (!isPrevDisabled) {
      setSelectedReviewId(infinitePhotoReviewList[prevReviewIndex].reviewId);
    }
  };
  const handleNextClick = () => {
    if (!isNextDisabled) {
      setSelectedReviewId(infinitePhotoReviewList[nextReviewIndex].reviewId);
    }
  };
  let t11;
  let t12;
  if ($[21] !== fetchNextPage || $[22] !== isLastReview) {
    t11 = () => {
      if (isLastReview) {
        fetchNextPage();
      }
    };
    t12 = [isLastReview, fetchNextPage];
    $[21] = fetchNextPage;
    $[22] = isLastReview;
    $[23] = t11;
    $[24] = t12;
  } else {
    t11 = $[23];
    t12 = $[24];
  }
  useEffect(t11, t12);
  const t13 = "grow";
  const t14 = selectedReviewId ? <div className="min-h-[544px]"><ReviewDetailContent reviewId={selectedReviewId} /></div> : <><div className={cn("relative grid min-h-[544px] grid-cols-7 gap-8", "mobile-tablet:h-full mobile-tablet:grid-cols-3")}>{infinitePhotoReviewList.map(t15 => {
        const {
          fileUrl: file_url,
          reviewId: review_id,
          productName: product_name
        } = t15;
        return <PhotoReviewItem key={review_id} alt={product_name} src={file_url} onClick={() => handleDetailReviewId(review_id)} />;
      })}</div>{hasNextPage ? <div className="my-30 w-full flex-center" ref={ref}><Spinner /></div> : null}</>;
  let t16;
  if ($[25] !== t14) {
    t16 = <div ref={containerRef} className={t13}>{t14}</div>;
    $[25] = t14;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== buttonPositions || $[28] !== handleNextClick || $[29] !== handlePrevClick || $[30] !== isNextDisabled || $[31] !== isPrevDisabled || $[32] !== selectedReviewId) {
    t17 = selectedReviewId ? <SideNavButtons positions={buttonPositions} onNextClick={handleNextClick} onPrevClick={handlePrevClick} isPrevDisabled={isPrevDisabled} isNextDisabled={isNextDisabled} /> : null;
    $[27] = buttonPositions;
    $[28] = handleNextClick;
    $[29] = handlePrevClick;
    $[30] = isNextDisabled;
    $[31] = isPrevDisabled;
    $[32] = selectedReviewId;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t16 || $[35] !== t17) {
    t18 = <>{t16}{t17}</>;
    $[34] = t16;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  return t18;
};
function _temp(page) {
  return page.context?.contents ?? [];
}